import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState, useContext } from 'react';
import { ModalContext } from 'app/app';
import { Download } from 'feather-icons-react';
import CW from './images/coverwhale_logo_white.png';
import { IconedButton } from 'components/IconedButton';
import { Loader, useAPI, ViewContext } from 'components/lib';
import { Form, Formik } from 'formik';
import { Edit } from 'feather-icons-react/build/IconComponents';
import styles from './styles.module.scss';
import DateHelper from "../../../helpers/dateUtils";
import * as Tooltip from "@radix-ui/react-tooltip";
import NumberUtils from "../../../helpers/numberUtils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../css/quillCustom.css';

export default function BindRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const user = useAPI('/api/user');
  const account = useAPI('/api/account');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [bindReqData, setBindReqData] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const dateFormatter = new DateHelper();
  const numberUtils = new NumberUtils();

  async function fetchBindRequest() {
    setLoading(true);
    await axios
      .get(`/api/bind-requests/admin/${id}`)
      .then(async (res) => {
        const bindReqData = res.data.data;
        setBindReqData(bindReqData);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchBindRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      bindReqData &&
      (bindReqData.issues !== undefined || null)
    ) {
      if (
        bindReqData.issues.length > 0 &&
        !bindReqData.messages
      ) {
        setModal({
          name: 'bind_request_issue_modal',
          data: {
            assignedAgentEmail: bindReqData.assignedAgentEmail,
          },
        });
      }
    }
  }, [setModal, bindReqData]);

  const handleSubmit = async (values) => {
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('sender', user.data.email);
      formData.append('receiver', 'hello@coverbadger.com');
      formData.append('title', values.title);
      if (bindReqData.ticketStatus === 'Pending') {
        formData.append('ticketStatus', 'In Progress');
      }
      formData.append('description', values.description);
      [...selectedFiles].forEach((file, i) => {
        formData.append(`documents`, file, file.name);
      });

      const response = await axios.put(
        `/api/bind-requests/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.success) {
        context.notification.show(
          'Message sent successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        setModal('');
      }
    } catch (error) {
      setSending(false);
      context.notification.show(
        error.message ||
        'There was a problem when sending the message.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      fetchBindRequest();
      setSending(false);
    }
  };

  if (user.loading || loading) return <Loader />;
  if (error) {
    return <div>Error loading bind request data: {error.message}</div>;
  }
  if (!bindReqData) {
    return <div>No bind request data available</div>;
  }

  return (
    <>
      <div className="w-full flex items-center justify-left">
        <IconedButton
          size="130px"
          color="#d32425"
          textColor="white"
          icon="arrow-left"
          text="Go back"
          action={() => {
            navigate('/app/bind-requests');
          }}
        />
      </div>

      <main className="flex-1 flex flex-col md:flex-row gap-4 max-h-full mt-2">
        {/* left */}
        <div className="w-full md:w-1/2">
          <div
            className="
            mb-6 rounded-[12px] w-full mx-auto
            bg-white pb-6 max-w-screen-xl
            "
            id="14"
          >
            <header
              className="
              bg-black flex items-center
              justify-between px-4 pb-2
              pt-2 rounded-t-[12px]
              "
            >
              <img src={CW} alt="Cover Whale" className="w-[360px]" />
            </header>

            <div
              className="
              flex-col border rounded
              p-4 mt-4 pt-0 border-slate-300 mx-6
              "
            >
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Bind Request Number</div>
                <div>{bindReqData.bindNumber}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Submission / Policy</div>
                <div>{bindReqData.submissionNumber}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Agency Name</div>
                <div>{bindReqData.agencyName}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Agent email</div>
                <div>{bindReqData.assignedAgentEmail}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Agent Phone</div>
                <div>{account?.loading ? 'Loading...' : account.data?.certificate_contacts[0]?.phone}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Name of insured</div>
                <div>{bindReqData.companyName}</div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Effective Date</div>
                <div>
                  {bindReqData.effectiveDate
                    ? new Date(bindReqData.effectiveDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })
                    : 'N/A'}
                </div>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-300">
                <div>Total Cost</div>
                <div>{numberUtils.formatToCurrency(bindReqData.totalCost)}</div>
              </div>
              <div className="space-y-2">
                {[
                  {
                    question: "Does the insured do any home deliveries including but not limited to moving operations?",
                    answer: bindReqData.answerQuestion1
                  },
                  {
                    question: "Does the insured visit construction sites at any time as part of their operation?",
                    answer: bindReqData.answerQuestion2
                  },
                  {
                    question: "Please provide in detail, what the insured hauls (General freight is not considered detailed)",
                    answer: bindReqData.answerQuestion3
                  },
                  {
                    question: "Is the insured connected to any other entity or share drivers, equipment or vehicles with any other entity?",
                    answer: bindReqData.answerQuestion4
                  },
                ].map((item, index) => (
                  <div key={index}
                    className={`flex justify-between ${index === 3 ? 'pt-2' : 'py-2 border-b border-slate-300'}`}>
                    <div className="w-3/5">{item.question}</div>
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger
                          className="max-w-[200px] truncate text-ellipsis overflow-hidden cursor-pointer hover:underline">
                          {item.answer}
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content className="max-w-[400px] bg-white p-3 shadow-lg rounded-lg border">
                            {item.answer}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 pl-6 mt-4 pt-0">
              Issues that need to be addressed:
            </div>

            <div className="flex-col border rounded p-4 pt-0 border-slate-300 mx-6">
              {bindReqData.issues.map((issue, index) => (
                <div
                  key={index}
                  className="
                  flex justify-between py-2 border-b border-slate-300"
                >
                  <div className="inline-flex items-center">
                    {issue.value}
                  </div>
                  <div className="inline-flex items-center">
                    <label className={styles.label}>
                      <span className={styles.checkbox_container}>
                        <input
                          type="checkbox"
                          checked={issue.solved}
                          readOnly
                          className={styles.checkbox}
                        />
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={styles.icon}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          stroke="currentColor"
                          stroke-width="1"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right */}
        <div className="w-full md:w-1/2">
          <div
            class="
              w-full bg-white flex
              items-center justify-center
              rounded-[12px]
            "
          >
            <div className="w-full mt-[20px] mb-[20px]">
              <div className="max-h-[800px] flex flex-col">
                <div
                  className="
                    w-full flex justify-between
                    items-center mb-4 pb-2 px-4
                    border-b border-slate-300
                  "
                >
                  <div>
                    <p className="font-bold">Cover Badger Support</p>
                    <p className="text-sm">hello@coverbadger.com</p>
                    <p className="text-sm">
                      {dateFormatter.timestamp(bindReqData.updatedAt)}
                    </p>
                  </div>
                </div>

                {/* Ticket messages */}
                <div
                  className="
                    flex-1 overflow-y-scroll px-4
                    py-2 flex flex-col-reverse
                  "
                >
                  {bindReqData.messages
                    .slice()
                    .sort(
                      (a, b) =>
                        new Date(b.sendAt) - new Date(a.sendAt)
                    )
                    .map((message, index) => (
                      <div
                        key={index}
                        className="bg-gray-100 rounded-md p-2 mb-2 w-full"
                      >
                        <div id="title" className="mb-2 p-1">
                          Subject: {message.title}
                        </div>
                        <div
                          className="whitespace-pre-wrap p-1"
                          dangerouslySetInnerHTML={{ __html: message.description }}
                        />
                        <br />
                        <div
                          className="
                            w-full flex justify-between
                            items-center p-1
                          "
                        >
                          <div>{message.sender}</div>
                          <div>
                            {dateFormatter.timestamp(message.sendAt)}
                          </div>
                        </div>
                        {message.documents.length > 0 && (
                          <div className="mt-2 mb-2">
                            <ul className="flex flex-wrap">
                              {message.documents.map(
                                (document, index) => {
                                  const urlParts =
                                    document.split('/');
                                  const fileNameWithTimestamp =
                                    urlParts[urlParts.length - 1];
                                  const fileName =
                                    fileNameWithTimestamp.split(
                                      '-'
                                    )[0];
                                  const decodedFileName =
                                    decodeURIComponent(fileName);
                                  const truncatedDocument =
                                    decodedFileName.length > 18
                                      ? decodedFileName.substring(
                                        0,
                                        18
                                      ) + '...'
                                      : decodedFileName;

                                  return (
                                    <label
                                      key={index}
                                      htmlFor="custom-input"
                                      className="
                                          text-md text-white mt-2 mr-2 py-2
                                          px-4 rounded-md border-0 bg-[#d32425]
                                          cursor-pointer max-w-[180px] flex
                                          justify-center items-center
                                    "
                                      title="click to download file"
                                    >
                                      <a
                                        href={document}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="
                                      text-white no-underline truncate
                                      flex items-center whitespace-nowrap
                                      overflow-hidden text-ellipsis
                                      "
                                      >
                                        <span
                                          style={{
                                            marginRight: '4px',
                                          }}
                                        >
                                          {truncatedDocument}
                                        </span>
                                        <Download
                                          className="text-white"
                                          size={16}
                                        />
                                      </a>
                                    </label>
                                  );
                                }
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {bindReqData.ticketStatus === 'Completed' ? null : (
                  <Formik
                    initialValues={{
                      title: '',
                      description: '',
                      documents: [],
                    }}
                    onSubmit={handleSubmit}
                  >
                    {({ handleChange, values, setFieldTouched, setFieldValue }) => (
                      <Form encType="multipart/form-data">
                        <div className="w-full mb-4 px-4 py-2">
                          <input
                            type="text"
                            className="
                            w-full p-2 border border-slate-300
                            rounded-lg mb-2"
                            placeholder="Subject..."
                            name="title"
                            onChange={handleChange}
                            disabled={
                              bindReqData.ticketStatus === 'Completed'
                            }
                          />

                          <ReactQuill
                            value={values.description}
                            onChange={(content) => {
                              setFieldValue('description', content);
                              setFieldTouched('description', true, false);
                            }}
                            modules={{
                              toolbar: [
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                ['clean']
                              ],
                            }}
                            formats={[
                              'bold', 'italic', 'underline', 'strike',
                              'list', 'bullet'
                            ]}
                            placeholder="Write a message"
                          />

                          <ul className="flex flex-wrap">
                            {Array.from(selectedFiles).map((file) => (
                              <label
                                htmlFor="custom-input"
                                className="
                                flex text-md text-white mt-2 mr-2 py-2 px-4
                                rounded-md border-0 bg-[#d32425]
                                cursor-pointer w-[200px]
                                "
                                title="Click to change file"
                              >
                                <div className="flex items-center">
                                  <span
                                    style={{ marginRight: '8px' }}
                                  >
                                    {file.name.length > 18
                                      ? file.name.substring(0, 18) +
                                      '...'
                                      : file.name}
                                  </span>
                                  <Edit
                                    className="text-white"
                                    size={16}
                                  />
                                </div>
                              </label>
                            ))}
                          </ul>
                        </div>

                        {bindReqData.ticketStatus ===
                          'Completed' ? null : (
                          <div className="w-full flex justify-between px-4 py-2">
                            <div className="flex flex-row items-center">
                              <input
                                type="file"
                                id="custom-input"
                                onChange={(event) => {
                                  setSelectedFiles(
                                    event.target.files
                                  );
                                }}
                                hidden
                                multiple
                                disabled={
                                  bindReqData.ticketStatus ===
                                  'Completed'
                                }
                              />
                              <label
                                htmlFor="custom-input"
                                className="
                                block text-md text-white mr-4 py-2
                                px-4 rounded-md border-0 bg-[#d32425]
                                cursor-pointer disabled:cursor-not-allowed
                                "
                                disabled={
                                  bindReqData.ticketStatus ===
                                  'Completed'
                                }
                              >
                                Attach one or more documents
                              </label>
                            </div>

                            <button
                              className="
                              bg-[#d32425] text-white px-4 py-2
                              rounded-md disabled:bg-gray-200
                              disabled:cursor-not-allowed
                              "
                              type="submit"
                              disabled={
                                sending ||
                                bindReqData.ticketStatus ===
                                'Completed' ||
                                !values.title
                              }
                            >
                              {sending ? 'Sending...' : 'Send'}
                            </button>
                          </div>
                        )}
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
